import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import React, { useCallback } from 'react';
import {
  ScrollView,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';

import { IQESAnswerOption, IQESFeedbackCard, IQESParchmentCard, IQESSceneBackdrop } from './IQESComponents';
import {
  iqesColors,
  iqesGlow,
  iqesGradients,
  iqesInteractionStates,
  iqesRadii,
  iqesTypography,
} from './iqesDesignSystem';
import { type IQESSceneKey } from './iqesSceneSystem';

type FormatKind = 'mcq' | 'multi' | 'trueFalse' | 'blank' | 'ordering';

type QuestionCard = {
  badge: string;
  title: string;
  subtitle: string;
  category: string;
  difficulty: string;
  streak: number;
  gems: number;
  timer: number;
  progress: string;
  prompt: string;
  reference: string;
  kind: FormatKind;
  sceneKey: IQESSceneKey;
  answers?: Array<{ label: string; selected?: boolean; muted?: boolean }>;
  feedbackTitle: string;
  feedbackBody: string;
  feedbackXp: number;
};

const cards: QuestionCard[] = [
  {
    badge: 'A',
    title: 'MULTIPLE CHOICE',
    subtitle: 'SINGLE ANSWER',
    category: 'New Testament',
    difficulty: 'Easy',
    streak: 6,
    gems: 1250,
    timer: 12,
    progress: '3/10',
    prompt: 'Who walked on water\nto go to Jesus?',
    reference: 'Matthew 14:25',
    kind: 'mcq',
    sceneKey: 'stormSea',
    answers: [
      { label: 'Peter', selected: true },
      { label: 'John' },
      { label: 'James' },
      { label: 'Andrew' },
    ],
    feedbackTitle: 'CORRECT!',
    feedbackBody: 'Well done! You chose the right answer.',
    feedbackXp: 25,
  },
  {
    badge: 'B',
    title: 'MULTIPLE SELECT',
    subtitle: 'MULTIPLE ANSWERS',
    category: 'Old Testament',
    difficulty: 'Medium',
    streak: 8,
    gems: 1250,
    timer: 15,
    progress: '4/10',
    prompt: 'Select ALL the fruits\nof the Spirit.',
    reference: 'Galatians 5:22-23',
    kind: 'multi',
    sceneKey: 'spiritStorm',
    answers: [
      { label: 'Love', selected: true },
      { label: 'Joy', selected: true },
      { label: 'Peace', selected: true },
      { label: 'Patience', selected: true },
      { label: 'Kindness', selected: true },
      { label: 'Anger', muted: true },
      { label: 'Pride', muted: true },
      { label: 'Greed', muted: true },
    ],
    feedbackTitle: 'GREAT!',
    feedbackBody: 'You selected all the correct answers.',
    feedbackXp: 30,
  },
  {
    badge: 'C',
    title: 'TRUE / FALSE',
    subtitle: '',
    category: 'New Testament',
    difficulty: 'Easy',
    streak: 5,
    gems: 1250,
    timer: 10,
    progress: '2/10',
    prompt: 'Jesus was born\nin Bethlehem.',
    reference: 'Luke 2:4-7',
    kind: 'trueFalse',
    sceneKey: 'parchment',
    feedbackTitle: 'NOT QUITE',
    feedbackBody: 'The correct answer is TRUE. Do not give up!',
    feedbackXp: 0,
  },
  {
    badge: 'D',
    title: 'FILL IN THE BLANK',
    subtitle: '',
    category: 'New Testament',
    difficulty: 'Medium',
    streak: 7,
    gems: 1250,
    timer: 18,
    progress: '5/10',
    prompt: 'For God so loved\nthe ________,\nthat he gave his one\nand only Son.',
    reference: 'John 3:16',
    kind: 'blank',
    sceneKey: 'sunsetCross',
    feedbackTitle: 'EXCELLENT!',
    feedbackBody: 'That is correct! You completed the verse.',
    feedbackXp: 25,
  },
  {
    badge: 'E',
    title: 'ORDERING / SEQUENCE',
    subtitle: '',
    category: 'Old Testament',
    difficulty: 'Hard',
    streak: 9,
    gems: 1250,
    timer: 20,
    progress: '6/10',
    prompt: 'Arrange these events in\nthe order they happened.',
    reference: '',
    kind: 'ordering',
    sceneKey: 'templeOrdering',
    feedbackTitle: 'PERFECT ORDER!',
    feedbackBody: 'Beautiful! You arranged them correctly.',
    feedbackXp: 30,
  },
];

const features = [
  ['book', 'Scripture Focused', 'Every question points to God’s Word.'],
  ['image', 'Immersive Visuals', 'Designed for wonder and engagement.'],
  ['happy', 'Encouraging Feedback', 'Celebrate learning. Build confidence.'],
  ['people', 'Works for Everyone', 'Kids, youth, adults, families & churches.'],
  ['globe', 'Multiple Languages', 'Play and learn in your language.'],
  ['wifi', 'Play Anywhere', 'Solo, Family or Congregation.'],
] as const;

const IQESPrototypeScreen = () => {
  const navigation = useNavigation();
  const { width } = useWindowDimensions();
  const isWide = width >= 980;
  const cardWidth = isWide ? Math.max(210, Math.min(318, (width - 100) / 5)) : Math.min(width - 32, 360);

  useFocusEffect(
    useCallback(() => {
      const parent = navigation.getParent();
      parent?.setOptions({
        headerShown: false,
        tabBarStyle: { display: 'none' },
      });

      return () => {
        parent?.setOptions({
          headerShown: true,
          tabBarStyle: undefined,
        });
      };
    }, [navigation])
  );

  return (
    <View style={styles.root}>
      <LinearGradient colors={iqesGradients.appBackground} style={StyleSheet.absoluteFill} />
      <View style={styles.goldHaze} />
      <View style={styles.purpleHaze} />
      <ScrollView contentContainerStyle={styles.scroll} showsVerticalScrollIndicator={false}>
        <View style={styles.heroTitleWrap}>
          <Text style={styles.heroTitle}>ALL QUESTION TYPES - ONE IMMERSIVE EXPERIENCE</Text>
          <Text style={styles.heroSubtitle}>Engaging. Scripture-Centered. Beautifully Interactive.</Text>
        </View>

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={[styles.cardRail, isWide && styles.cardRailWide]}
        >
          {cards.map(card => (
            <View key={card.badge} style={[styles.formatColumn, { width: cardWidth }]}>
              <View style={styles.cardHeading}>
                <View style={styles.badgeOrb}>
                  <Text style={styles.badgeOrbText}>{card.badge}</Text>
                </View>
                <View>
                  <Text style={styles.cardTitle}>{card.title}</Text>
                  {!!card.subtitle && <Text style={styles.cardSubtitle}>({card.subtitle})</Text>}
                </View>
              </View>

              <LinearGradient colors={iqesGradients.deviceFrame} style={styles.deviceFrame}>
                <View style={styles.topStats}>
                  <View style={styles.streakPill}>
                    <Text style={styles.streakLabel}>STREAK</Text>
                    <Text style={styles.streakValue}>🔥 x {card.streak}</Text>
                  </View>
                  <View style={styles.categoryBlock}>
                    <Text style={styles.categoryText}>{card.category}</Text>
                    <Text style={styles.difficultyText}>{card.difficulty}</Text>
                  </View>
                  <View style={styles.gemPill}>
                    <Text style={styles.gemIcon}>◆</Text>
                    <Text style={styles.gemText}>{card.gems}</Text>
                  </View>
                </View>

                <QuestionSurface card={card} />

                <View style={styles.bottomHud}>
                  <View style={styles.timerDial}>
                    <Text style={styles.timerNumber}>{card.timer}</Text>
                    <Text style={styles.timerUnit}>SEC</Text>
                  </View>
                  <View style={styles.progressPill}>
                    <Text style={styles.progressText}>{card.progress}</Text>
                    <View style={styles.progressTrack}>
                      <View style={styles.progressFill} />
                    </View>
                  </View>
                  <View style={styles.hintPill}>
                    <Text style={styles.hintText}>💡 HINT</Text>
                    <Text style={styles.hintCost}>◆ -5</Text>
                  </View>
                </View>
              </LinearGradient>

              <Text style={styles.helperCopy}>{helperCopy(card.kind)}</Text>
              <View style={styles.dotRail}>
                <View style={styles.dotActive} />
                <View style={styles.dot} />
                <View style={styles.dot} />
              </View>
              <FeedbackCard card={card} />
            </View>
          ))}
        </ScrollView>

        <View style={styles.featureBar}>
          {features.map(([icon, title, body]) => (
            <View key={title} style={styles.featureItem}>
              <Ionicons name={icon as keyof typeof Ionicons.glyphMap} size={30} color={iqesColors.gold} />
              <View style={styles.featureCopy}>
                <Text style={styles.featureTitle}>{title}</Text>
                <Text style={styles.featureBody}>{body}</Text>
              </View>
            </View>
          ))}
        </View>
      </ScrollView>
    </View>
  );
};

const QuestionSurface = ({ card }: { card: QuestionCard }) => {
  if (card.kind === 'trueFalse') {
    return (
      <View style={styles.sceneArea}>
        <IQESParchmentCard prompt={card.prompt} reference={card.reference} />
        <View style={styles.trueFalseRow}>
          <ChoiceTile tone="green" icon="checkmark" label="TRUE" />
          <ChoiceTile tone="red" icon="close" label="FALSE" />
        </View>
      </View>
    );
  }

  if (card.kind === 'blank') {
    return (
      <View style={styles.sceneArea}>
        <IQESSceneBackdrop sceneKey={card.sceneKey} style={[styles.sceneBackdrop, styles.topSceneBackdrop]} />
        <IQESParchmentCard prompt={card.prompt} reference={card.reference} compact />
        <View style={styles.answerInput}>
          <Text style={styles.answerInputText}>world</Text>
        </View>
        <View style={styles.keyboardMock}>
          {'QWERTYUIOPASDFGHJKLZXCVBNM'.slice(0, 24).split('').map((letter, index) => (
            <View key={`${letter}-${index}`} style={styles.keycap}>
              <Text style={styles.keycapText}>{letter}</Text>
            </View>
          ))}
        </View>
      </View>
    );
  }

  if (card.kind === 'ordering') {
    return (
      <View style={styles.sceneArea}>
        <IQESSceneBackdrop sceneKey={card.sceneKey} style={[styles.sceneBackdrop, styles.fullSceneBackdrop]} />
        <Text style={styles.promptText}>{card.prompt}</Text>
        {['The Exodus', 'The Creation', 'The Flood', 'The Tower of Babel'].map((item, index) => (
          <View key={item} style={styles.orderRow}>
            <View style={styles.orderNumber}>
              <Text style={styles.orderNumberText}>{[2, 1, 3, 4][index]}</Text>
            </View>
            <Text style={styles.orderText}>{item}</Text>
          </View>
        ))}
        <Text style={styles.dragHint}>Tap two answers to swap</Text>
      </View>
    );
  }

  return (
    <View style={styles.sceneArea}>
      <IQESSceneBackdrop sceneKey={card.sceneKey} style={[styles.sceneBackdrop, styles.topSceneBackdrop]} />
      <Text style={styles.promptText}>{card.prompt}</Text>
      {!!card.reference && <Text style={styles.referenceText}>{card.reference}</Text>}
      <View style={card.kind === 'multi' ? styles.multiGrid : styles.answerStack}>
        {card.answers?.map((answer, index) => (
          <IQESAnswerOption
            key={answer.label}
            label={answer.label}
            index={card.kind === 'mcq' ? index : undefined}
            state={answer.selected ? 'selected' : answer.muted ? 'disabled' : 'default'}
            trailingIcon={card.kind === 'multi' && answer.selected ? 'checkmark' : undefined}
            style={[
              card.kind === 'multi' ? styles.multiAnswer : styles.answerRow,
            ]}
          />
        ))}
      </View>
      {card.kind === 'multi' && <Text style={styles.selectionCount}>5 of 8 selected</Text>}
    </View>
  );
};

const ChoiceTile = ({ tone, icon, label }: { tone: 'green' | 'red'; icon: 'checkmark' | 'close'; label: string }) => (
  <LinearGradient
    colors={tone === 'green' ? iqesGradients.trueChoice : iqesGradients.falseChoice}
    style={styles.choiceTile}
  >
    <Ionicons name={icon} size={44} color={tone === 'green' ? '#FFF0A8' : '#FF8B72'} />
    <Text style={styles.choiceLabel}>{label}</Text>
  </LinearGradient>
);

const FeedbackCard = ({ card }: { card: QuestionCard }) => {
  const isWrong = card.feedbackXp === 0;
  return (
    <IQESFeedbackCard
      title={card.feedbackTitle}
      body={card.feedbackBody}
      xp={card.feedbackXp}
      tone={isWrong ? 'incorrect' : 'correct'}
    />
  );
};

const helperCopy = (kind: FormatKind) => {
  switch (kind) {
    case 'multi':
      return 'Choose all that apply. More than one is right!';
    case 'trueFalse':
      return 'Decide if the statement is True or False.';
    case 'blank':
      return 'Type the missing word to complete the verse.';
    case 'ordering':
      return 'Tap one item, then tap another to swap.';
    default:
      return 'Tap the correct answer. One choice is right!';
  }
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: iqesColors.canvas,
  },
  scroll: {
    paddingTop: 20,
    paddingBottom: 28,
  },
  goldHaze: {
    position: 'absolute',
    left: -120,
    bottom: 120,
    width: 280,
    height: 280,
    borderRadius: 140,
    backgroundColor: 'rgba(245, 158, 11, 0.08)',
  },
  purpleHaze: {
    position: 'absolute',
    right: -80,
    top: 80,
    width: 260,
    height: 260,
    borderRadius: 130,
    backgroundColor: 'rgba(103, 58, 183, 0.12)',
  },
  heroTitleWrap: {
    alignItems: 'center',
    paddingHorizontal: 18,
    marginBottom: 10,
  },
  heroTitle: {
    color: iqesColors.goldSoft,
    ...iqesTypography.title,
    textAlign: 'center',
    fontFamily: iqesTypography.displayFamily,
  },
  heroSubtitle: {
    color: iqesColors.ivory,
    fontSize: 18,
    lineHeight: 24,
    textAlign: 'center',
    marginTop: 2,
  },
  cardRail: {
    gap: 10,
    paddingHorizontal: 12,
    paddingTop: 2,
  },
  cardRailWide: {
    justifyContent: 'center',
  },
  formatColumn: {
    minWidth: 0,
  },
  cardHeading: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 58,
    gap: 12,
  },
  badgeOrb: {
    width: 42,
    height: 42,
    borderRadius: 21,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: iqesColors.royalPurple,
    borderWidth: 1,
    borderColor: iqesColors.purpleGlow,
  },
  badgeOrbText: {
    color: '#F5E8FF',
    fontSize: 24,
    fontWeight: '800',
  },
  cardTitle: {
    color: '#FFFFFF',
    fontSize: 15,
    lineHeight: 18,
    fontWeight: '800',
    letterSpacing: 0.4,
  },
  cardSubtitle: {
    color: '#FFFFFF',
    fontSize: 12,
    lineHeight: 15,
    fontWeight: '700',
    letterSpacing: 0.3,
  },
  deviceFrame: {
    height: 548,
    borderRadius: iqesRadii.device,
    borderWidth: 1,
    borderColor: 'rgba(245, 158, 11, 0.58)',
    padding: 14,
    overflow: 'hidden',
    ...iqesGlow.device,
  },
  topStats: {
    height: 58,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: 18,
    backgroundColor: 'rgba(255,255,255,0.045)',
    paddingHorizontal: 8,
  },
  streakPill: {
    minWidth: 64,
  },
  streakLabel: {
    color: '#B8A38E',
    fontSize: 10,
    letterSpacing: 0.8,
  },
  streakValue: {
    color: '#FFECA7',
    fontSize: 17,
    fontWeight: '800',
  },
  categoryBlock: {
    alignItems: 'center',
    flex: 1,
  },
  categoryText: {
    color: iqesColors.goldSoft,
    fontSize: 13,
    fontWeight: '800',
  },
  difficultyText: {
    color: iqesColors.gold,
    fontSize: 13,
    marginTop: 2,
  },
  gemPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 9,
    paddingVertical: 7,
    borderRadius: 16,
    backgroundColor: 'rgba(16, 14, 30, 0.7)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
  },
  gemIcon: {
    color: '#B55CFF',
    fontSize: 14,
  },
  gemText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '700',
  },
  sceneArea: {
    flex: 1,
    marginTop: 12,
    alignItems: 'center',
  },
  sceneBackdrop: {
    position: 'absolute',
    overflow: 'hidden',
    backgroundColor: iqesColors.canvasRaised,
  },
  sceneBackdropImage: {
    borderRadius: iqesRadii.panel,
  },
  topSceneBackdrop: {
    top: 0,
    left: 0,
    right: 0,
    height: 172,
    borderRadius: iqesRadii.panel,
  },
  fullSceneBackdrop: {
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: iqesRadii.panel,
  },
  promptText: {
    color: '#FFFFFF',
    ...iqesTypography.question,
    textAlign: 'center',
    marginTop: 70,
    fontFamily: 'Georgia',
  },
  referenceText: {
    color: iqesColors.gold,
    fontSize: 12,
    marginTop: 8,
    marginBottom: 12,
  },
  answerStack: {
    width: '100%',
    gap: 8,
    marginTop: 10,
  },
  answerRow: {
    minHeight: 46,
    borderRadius: iqesRadii.answer,
    borderWidth: 1,
    ...iqesInteractionStates.resting,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 9,
  },
  answerSelected: {
    ...iqesInteractionStates.selected,
    ...iqesGlow.gold,
  },
  answerMuted: {
    opacity: 0.72,
  },
  answerLetter: {
    width: 26,
    height: 26,
    borderRadius: 13,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#D4A33A',
    marginRight: 10,
  },
  answerLetterText: {
    color: iqesColors.goldSoft,
    fontSize: 13,
    fontWeight: '800',
  },
  answerText: {
    color: '#FFFFFF',
    fontSize: 14,
    flex: 1,
  },
  multiGrid: {
    width: '100%',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 18,
  },
  multiAnswer: {
    width: '48%',
    minHeight: 42,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(245, 158, 11, 0.42)',
    backgroundColor: 'rgba(16, 17, 34, 0.88)',
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 9,
    gap: 6,
  },
  checkText: {
    color: '#FFF0A8',
    fontSize: 20,
    fontWeight: '900',
  },
  selectionCount: {
    color: '#FFF2C1',
    fontSize: 12,
    marginTop: 14,
  },
  parchment: {
    width: '84%',
    minHeight: 138,
    marginTop: 22,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 18,
    paddingVertical: 16,
    overflow: 'hidden',
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#A56424',
    ...iqesGlow.parchment,
  },
  parchmentImage: {
    borderRadius: 8,
  },
  parchmentCompact: {
    marginTop: 46,
    minHeight: 168,
  },
  parchmentText: {
    color: iqesColors.parchmentInk,
    ...iqesTypography.verse,
    fontFamily: iqesTypography.displayFamily,
    textAlign: 'center',
  },
  parchmentTextCompact: {
    fontSize: 18,
    lineHeight: 27,
  },
  parchmentReference: {
    color: '#1B1007',
    fontSize: 12,
    fontWeight: '700',
    marginTop: 10,
  },
  trueFalseRow: {
    flexDirection: 'row',
    gap: 10,
    width: '100%',
    marginTop: 18,
  },
  choiceTile: {
    flex: 1,
    height: 150,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  choiceLabel: {
    color: iqesColors.goldSoft,
    fontSize: 22,
    fontWeight: '900',
    fontFamily: 'Georgia',
    marginTop: 8,
  },
  answerInput: {
    width: '88%',
    minHeight: 46,
    marginTop: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(245,158,11,0.5)',
    backgroundColor: 'rgba(8, 6, 10, 0.9)',
    justifyContent: 'center',
    paddingHorizontal: 12,
  },
  answerInputText: {
    color: '#FFFFFF',
    fontSize: 20,
  },
  keyboardMock: {
    width: '100%',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: 10,
  },
  keycap: {
    width: '9%',
    height: 28,
    borderRadius: 5,
    backgroundColor: '#2A2A31',
    alignItems: 'center',
    justifyContent: 'center',
  },
  keycapText: {
    color: '#FFFFFF',
    fontSize: 11,
    fontWeight: '700',
  },
  orderRow: {
    width: '100%',
    minHeight: 50,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(245,158,11,0.34)',
    backgroundColor: 'rgba(255,255,255,0.06)',
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 9,
    paddingHorizontal: 8,
  },
  orderNumber: {
    width: 38,
    height: 38,
    borderRadius: 19,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'rgba(245,158,11,0.45)',
    backgroundColor: 'rgba(0,0,0,0.35)',
    marginRight: 10,
  },
  orderNumberText: {
    color: iqesColors.goldSoft,
    fontSize: 20,
    fontWeight: '900',
  },
  orderText: {
    color: '#FFE8A3',
    fontSize: 15,
    fontFamily: 'Georgia',
  },
  dragHint: {
    color: '#FFFFFF',
    opacity: 0.85,
    fontSize: 12,
    marginTop: 18,
  },
  bottomHud: {
    height: 58,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  timerDial: {
    width: 60,
    height: 60,
    borderRadius: 30,
    borderWidth: 3,
    borderColor: iqesColors.gold,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.24)',
  },
  timerNumber: {
    color: iqesColors.goldSoft,
    fontSize: 22,
    fontWeight: '900',
    lineHeight: 24,
  },
  timerUnit: {
    color: iqesColors.goldSoft,
    fontSize: 9,
    fontWeight: '900',
  },
  progressPill: {
    flex: 1,
    height: 38,
    borderRadius: 19,
    borderWidth: 1,
    borderColor: 'rgba(245,158,11,0.64)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  progressText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '800',
  },
  progressTrack: {
    width: '70%',
    height: 3,
    borderRadius: 2,
    backgroundColor: 'rgba(255,255,255,0.2)',
    marginTop: 4,
  },
  progressFill: {
    width: '55%',
    height: '100%',
    borderRadius: 2,
    backgroundColor: iqesColors.gold,
  },
  hintPill: {
    minWidth: 56,
    minHeight: 46,
    borderRadius: 12,
    backgroundColor: '#5C1D83',
    borderWidth: 1,
    borderColor: 'rgba(216,180,254,0.45)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  hintText: {
    color: '#FFFFFF',
    fontSize: 10,
    fontWeight: '800',
  },
  hintCost: {
    color: iqesColors.goldSoft,
    fontSize: 12,
    fontWeight: '800',
  },
  helperCopy: {
    color: '#FFFFFF',
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
    marginTop: 12,
    minHeight: 40,
  },
  dotRail: {
    height: 18,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 22,
  },
  dot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#6B5D75',
  },
  dotActive: {
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: iqesColors.gold,
    shadowColor: iqesColors.gold,
    shadowOpacity: 0.6,
    shadowRadius: 9,
  },
  feedbackCard: {
    minHeight: 160,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(245,158,11,0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 14,
    overflow: 'hidden',
  },
  feedbackWrong: {
    borderColor: 'rgba(239,68,68,0.5)',
  },
  feedbackIconWrap: {
    position: 'absolute',
    left: 18,
    top: 22,
  },
  feedbackTitle: {
    color: '#9DF871',
    fontSize: 22,
    fontWeight: '900',
    fontFamily: 'Georgia',
    textAlign: 'center',
  },
  feedbackWrongTitle: {
    color: '#FCA5A5',
  },
  feedbackBody: {
    color: '#FFFFFF',
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
    marginTop: 8,
  },
  xpLine: {
    color: iqesColors.gold,
    fontSize: 24,
    fontWeight: '900',
    marginTop: 12,
  },
  xpBadge: {
    color: '#FFFFFF',
    fontSize: 14,
  },
  featureBar: {
    marginTop: 22,
    marginHorizontal: 22,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(245,158,11,0.55)',
    backgroundColor: 'rgba(255,255,255,0.045)',
    padding: 18,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 20,
    justifyContent: 'center',
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    minWidth: 210,
    maxWidth: 250,
  },
  featureCopy: {
    flex: 1,
  },
  featureTitle: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '800',
  },
  featureBody: {
    color: iqesColors.mutedWarm,
    fontSize: 13,
    lineHeight: 17,
    marginTop: 3,
  },
});

export default IQESPrototypeScreen;
